import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { AxiosResponse } from "axios";
import { postFollowList, postUnfollowList, List } from "../../../api/lists";
import { ServerSingleResponse } from "../../../types";
import { READING_LISTS_SHELF_QUERY_KEY } from "../../../screens/shelf/hooks/useReadingListsShelf";
import { readingListDetailQueryKey } from "../../../screens/shelf/ReadingListDetailScreen";

export interface UseToggleListFollowArgs {
  listId: number;
  isFollowing: boolean;
  // Called after a successful toggle with the new isFollowing state and the
  // server-authoritative follower count. Use it to update whatever cache
  // holds this list on the consuming surface (e.g. a creator-profile lists
  // query, a single-list view) — the detail-page cache is already kept in
  // sync by this hook.
  onSuccess?: (newIsFollowing: boolean, newFollowCount: number) => void;
}

// Cached shape of the detail query (mirrors ReadingListDetailScreen's
// useQuery, which holds the raw axios response). Typed locally so we can
// patch is_following / follow_count optimistically without spreading
// `unknown` through the mutation.
type DetailCache = AxiosResponse<ServerSingleResponse<List>>;

// Generic follow/unfollow mutation for reading lists. Auth gating and
// tracking are deliberately not built in — those belong to the surface that
// hosts the button (different surfaces want different funnel modals and
// analytics events). The shelf rail's "Following" tab is invalidated on
// every toggle so newly-followed/unfollowed lists show up there immediately.
export function useToggleListFollow({
  listId,
  isFollowing,
  onSuccess,
}: UseToggleListFollowArgs) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      isFollowing ? postUnfollowList(listId) : postFollowList(listId),
    onSuccess: (response) => {
      const newIsFollowing = !isFollowing;
      // Server returns the authoritative new follower count alongside the
      // success flag — read it instead of relying on a +1/-1 client guess
      // so concurrent follows from other clients don't drift our display.
      const newFollowCount = response.data.payload.results.follow_count;

      // Patch the detail-page cache directly so the follower-count stat on
      // the page the user is currently looking at flips instantly. Without
      // this we'd only invalidate the shelf rail below; the detail pane
      // would render stale until the next manual refetch.
      queryClient.setQueryData<DetailCache>(
        readingListDetailQueryKey(listId),
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            data: {
              ...prev.data,
              payload: {
                ...prev.data.payload,
                results: {
                  ...prev.data.payload.results,
                  is_following: newIsFollowing,
                  follow_count: newFollowCount,
                },
              },
            },
          };
        },
      );

      queryClient.invalidateQueries({ queryKey: READING_LISTS_SHELF_QUERY_KEY });
      onSuccess?.(newIsFollowing, newFollowCount);
    },
  });

  const toggle = useCallback(() => mutation.mutate(), [mutation]);

  return {
    toggle,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
